/**
 * Pointer and keyboard, translated into game intents.
 *
 * The only subtle part is `toBuffer`. The canvas is scaled and centred inside the
 * tube, so its CSS box is smaller than its parent and offset by the letterbox on
 * one axis. Measuring the *canvas* rect rather than the tube gets both the scale
 * and the offset for free, and it stays correct across a DPR change, a window
 * resize and a quality switch without any of them telling us.
 *
 * What the canvas rect does *not* give is the origin. The canvas shows the
 * rendered region, which on a window that is not 16:9 is wider or taller than
 * the 640x360 band and centred on it, so the top-left of the canvas is world
 * `(region.x0, region.y0)` and not `(0, 0)`. Mapping through the band's own size
 * would put every click off by half the extension — a build preview that lands
 * two tiles from the finger. The region is therefore read here, and the result
 * is still clamped to the band, because the band is the only place a tower can
 * stand however much world is on screen around it.
 *
 * Nothing here touches the world. Handlers receive buffer-space coordinates and
 * `main.ts` decides what they mean.
 */

import { VIEW } from '@/core/config';
import { region } from '@/core/region';

/** Buffer-space pointer state, rewritten in place. Never reallocated. */
export interface PointerState {
  /** Buffer pixels, fractional. Clamped to the buffer while inside. */
  x: number;
  y: number;
  /** True while the pointer is over the canvas. */
  inside: boolean;
  /** True while the primary button is held. */
  down: boolean;
  /** Client-space, for the shop's drag-to-place handoff. */
  clientX: number;
  clientY: number;
  /**
   * True when the last event came from a finger or a stylus.
   *
   * A mouse can hover, so it can be shown a preview before it commits and a
   * click may as well place immediately. A finger cannot: the first the player
   * sees of a placement is the frame the tile is already occupied. `main.ts`
   * reads this to defer the commit to release, so a touch drag becomes an
   * aim-then-let-go gesture with the validity overlay visible throughout.
   */
  touch: boolean;
}

export interface InputCallbacks {
  /** Any pointer or key. Fires once, then never again — audio unlock hangs off it. */
  onGesture?(): void;

  onPointerMove?(p: PointerState): void;
  /** `button` follows the DOM: 0 primary, 2 secondary. */
  onPointerDown?(p: PointerState, button: number): void;
  onPointerUp?(p: PointerState, button: number): void;
  onPointerLeave?(): void;
  /** The gesture was taken away — a system swipe, a call. Drop any preview. */
  onPointerCancel?(): void;

  /** Hotkeys 1-9 then 0, as index 0..9 into the shop. */
  onSelectSlot?(index: number): void;
  /** Escape: drop the build cursor, drop the selection, or pause. */
  onEscape?(): void;
  /** Space or Enter. */
  onStartRound?(): void;
  /** T, +shift to go backwards. */
  onCycleTargeting?(dir: 1 | -1): void;
  /** `+`/`-`. */
  onSpeed?(delta: 1 | -1): void;
  onTogglePause?(): void;
  onSell?(): void;
  /** Q, W, E map onto upgrade paths 0, 1, 2. */
  onUpgrade?(path: 0 | 1 | 2): void;
}

/** Editable fields swallow every key; a hotkey fired from one is always a bug. */
function typingInto(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

export class Input {
  readonly pointer: PointerState = {
    x: 0, y: 0, inside: false, down: false, clientX: 0, clientY: 0, touch: false,
  };

  /**
   * Set by `main.ts` while a modal owns the screen. Pointer events still track
   * so the cursor does not jump on resume; intents stop firing.
   */
  suspended = false;

  private attached = false;
  private gestured = false;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly cb: InputCallbacks = {},
  ) {}

  attach(): void {
    if (this.attached) return;
    this.attached = true;
    const c = this.canvas;
    c.addEventListener('pointermove', this.onMove);
    c.addEventListener('pointerdown', this.onDown);
    c.addEventListener('pointerleave', this.onLeave);
    c.addEventListener('contextmenu', this.onContextMenu);
    // Up is on the window: a drag that ends off the canvas must still release.
    window.addEventListener('pointerup', this.onUp);
    window.addEventListener('pointercancel', this.onCancel);
    window.addEventListener('keydown', this.onKey);
  }

  detach(): void {
    if (!this.attached) return;
    this.attached = false;
    const c = this.canvas;
    c.removeEventListener('pointermove', this.onMove);
    c.removeEventListener('pointerdown', this.onDown);
    c.removeEventListener('pointerleave', this.onLeave);
    c.removeEventListener('contextmenu', this.onContextMenu);
    window.removeEventListener('pointerup', this.onUp);
    window.removeEventListener('pointercancel', this.onCancel);
    window.removeEventListener('keydown', this.onKey);
  }

  /**
   * Client point → buffer point, written into `out`.
   *
   * Returns false when the point is outside the canvas box, in which case `out`
   * holds the clamped position rather than a stale one — a build preview that
   * freezes at the last good tile reads worse than one that slides to the edge.
   */
  toBuffer(clientX: number, clientY: number, out: { x: number; y: number }): boolean {
    const r = this.canvas.getBoundingClientRect();
    if (r.width < 1 || r.height < 1) {
      out.x = 0;
      out.y = 0;
      return false;
    }
    const fx = (clientX - r.left) / r.width;
    const fy = (clientY - r.top) / r.height;
    const inside = fx >= 0 && fx < 1 && fy >= 0 && fy < 1;

    const view = region.current;
    const bx = view.x0 + fx * view.width;
    const by = view.y0 + fy * view.height;
    out.x = bx < 0 ? 0 : bx > VIEW.width - 1 ? VIEW.width - 1 : bx;
    out.y = by < 0 ? 0 : by > VIEW.height - 1 ? VIEW.height - 1 : by;
    return inside;
  }

  private gesture(): void {
    if (this.gestured) return;
    this.gestured = true;
    this.cb.onGesture?.();
  }

  private track(ev: PointerEvent): void {
    const p = this.pointer;
    p.clientX = ev.clientX;
    p.clientY = ev.clientY;
    p.touch = ev.pointerType !== 'mouse';
    p.inside = this.toBuffer(ev.clientX, ev.clientY, p);
  }

  private readonly onMove = (ev: PointerEvent): void => {
    this.track(ev);
    if (this.suspended) return;
    this.cb.onPointerMove?.(this.pointer);
  };

  private readonly onDown = (ev: PointerEvent): void => {
    this.gesture();
    this.track(ev);
    if (ev.button === 0) this.pointer.down = true;
    if (this.suspended) return;
    // A finger that slides off the canvas mid-drag must keep steering the build
    // ghost, and without capture the browser stops delivering moves the moment
    // it crosses into the HUD. Mouse needs no such thing — it already gets
    // moves everywhere — and capturing it would swallow the rail's own hovers.
    if (this.pointer.touch) {
      try { this.canvas.setPointerCapture(ev.pointerId); } catch { /* stale id */ }
    }
    // Keeps focus off whatever button was last clicked, so Space does not
    // re-trigger it instead of starting the round.
    this.canvas.focus({ preventScroll: true });
    this.cb.onPointerDown?.(this.pointer, ev.button);
  };

  private readonly onUp = (ev: PointerEvent): void => {
    this.track(ev);
    if (ev.button === 0) this.pointer.down = false;
    if (this.canvas.hasPointerCapture?.(ev.pointerId)) {
      this.canvas.releasePointerCapture(ev.pointerId);
    }
    if (this.suspended) return;
    this.cb.onPointerUp?.(this.pointer, ev.button);
  };

  private readonly onCancel = (ev: PointerEvent): void => {
    this.pointer.down = false;
    this.pointer.inside = false;
    if (this.canvas.hasPointerCapture?.(ev.pointerId)) {
      this.canvas.releasePointerCapture(ev.pointerId);
    }
    if (this.suspended) return;
    this.cb.onPointerCancel?.();
  };

  private readonly onLeave = (): void => {
    this.pointer.inside = false;
    if (this.suspended) return;
    this.cb.onPointerLeave?.();
  };

  /** Right-click sells and deselects; the browser menu would eat both. */
  private readonly onContextMenu = (ev: MouseEvent): void => {
    ev.preventDefault();
  };

  private readonly onKey = (ev: KeyboardEvent): void => {
    if (ev.altKey || ev.ctrlKey || ev.metaKey) return;
    if (typingInto(ev.target)) return;
    this.gesture();

    // Escape has to survive suspension — it is how a modal gets closed.
    if (ev.key === 'Escape') {
      ev.preventDefault();
      this.cb.onEscape?.();
      return;
    }
    if (this.suspended) return;

      const code = ev.code;

    if (code.startsWith('Digit') || code.startsWith('Numpad')) {
      const digit = Number(code.slice(code.startsWith('Digit') ? 5 : 6));
      if (Number.isInteger(digit)) {
        ev.preventDefault();
        // 1..9 are slots 0..8 and 0 is the tenth, which is how every TD
        // hotbar since the nineties has been laid out.
        this.cb.onSelectSlot?.(digit === 0 ? 9 : digit - 1);
        return;
      }
    }

    /*
     * Targeting used to be bound to Tab, `preventDefault`ed on the window.
     *
     * Tab is the only key a keyboard user has for moving focus, and the canvas
     * carries `tabIndex = 0`, so the first Tab landed on the playfield and
     * every Tab after it was eaten: not one shop tile, upgrade pip or panel
     * button was reachable without a mouse, at any viewport, ever. Scoping the
     * capture to "only while the canvas has focus" does not help — that is
     * precisely the state Tab put the player in, and it made the trap airtight.
     *
     * T, so Tab means what Tab means. Nothing user-facing named the old key.
     */
    if (code === 'KeyT') {
      ev.preventDefault();
      this.cb.onCycleTargeting?.(ev.shiftKey ? -1 : 1);
      return;
    }

    switch (ev.key) {
      case ' ':
      case 'Enter':
        // On a focused button these already activate it; firing the round as
        // well means arming a tower from the keyboard also sends the wave.
        if (document.activeElement !== this.canvas) return;
        ev.preventDefault();
        this.cb.onStartRound?.();
        return;
      case '+':
      case '=':
        ev.preventDefault();
        this.cb.onSpeed?.(1);
        return;
      case '-':
      case '_':
        ev.preventDefault();
        this.cb.onSpeed?.(-1);
        return;
      default:
        break;
    }

    switch (code) {
      case 'KeyP':
        ev.preventDefault();
        this.cb.onTogglePause?.();
        return;
      case 'KeyQ':
        ev.preventDefault();
        this.cb.onUpgrade?.(0);
        return;
      case 'KeyW':
        ev.preventDefault();
        this.cb.onUpgrade?.(1);
        return;
      case 'KeyE':
        ev.preventDefault();
        this.cb.onUpgrade?.(2);
        return;
      case 'KeyX':
      case 'Backspace':
      case 'Delete':
        ev.preventDefault();
        this.cb.onSell?.();
        return;
      default:
        break;
    }
  };
}
